import { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import Typography from '@mui/material/Typography';
import { useSubmitCallbackMutation } from '../../store/siteContentApi';
import { UI, DISPLAY, EASE } from '../../styles/tl5';

// "Get a callback" — a number and roughly when to ring. Before this the
// card said "Callback requested ✓" and sent nothing (API-NOTES 2.2).
//
// The times are free text, not slots: we have no scheduler, so the card
// asks for a preference and the team rings when they can. Promising a
// specific time we cannot keep is worse than asking.
const WHEN = ['As soon as possible', 'This morning', 'This afternoon', 'This evening', 'Tomorrow'];

const tenDigits = (raw) => {
  const d = String(raw || '').replace(/\D/g, '');
  const ten = d.length > 10 ? d.slice(-10) : d;
  return /^[6-9]\d{9}$/.test(ten) ? ten : '';
};

const label = { fontFamily: UI, fontSize: 13.5, fontWeight: 700, color: 'var(--tl-g9)' };
const field = {
  width: '100%',
  font: 'inherit',
  fontFamily: UI,
  fontSize: 15,
  color: 'var(--tl-g9)',
  p: '12px 14px',
  borderRadius: '14px',
  border: '1.5px solid var(--tl-line)',
  bgcolor: '#FFFFFF',
  outline: 'none',
  '&:focus': { borderColor: 'var(--tl-teal)' },
};

const CallbackRequestDialog = ({ open, source = 'home', onClose, onDone }) => {
  const [submit, { isLoading }] = useSubmitCallbackMutation();
  const [phone, setPhone] = useState('');
  const [name, setName] = useState('');
  const [when, setWhen] = useState(WHEN[0]);
  const [error, setError] = useState('');
  const [done, setDone] = useState(null);

  // A fresh dialog each time — a previous success must not greet the
  // next person who opens it.
  useEffect(() => {
    if (open) { setError(''); setDone(null); }
  }, [open]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    const ten = tenDigits(phone);
    if (!ten) { setError('Enter a valid 10-digit mobile number.'); return; }
    try {
      const res = await submit({
        phone: ten,
        name: name.trim(),
        preferred_time: when,
        source,
      }).unwrap();
      setDone(res);
      onDone?.(res);
    } catch (err) {
      setError(err?.data?.message || 'Could not send that. Please try again, or call us.');
    }
  };

  return (
    <Dialog
      open={open}
      onClose={isLoading ? undefined : onClose}
      maxWidth="xs"
      fullWidth
      // PaperProps, not slotProps.paper — MUI 5.18 silently ignores the latter.
      PaperProps={{ sx: { borderRadius: '24px', m: 2 } }}
    >
      <Box sx={{ p: 'clamp(20px, 5vw, 28px)' }}>
        {done ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Box
              component="span"
              sx={{
                display: 'grid', placeItems: 'center', width: 46, height: 46,
                borderRadius: '50%', bgcolor: 'var(--tl-mint)', color: 'var(--tl-teal-d)',
                fontSize: 24, fontWeight: 800,
              }}
            >
              ✓
            </Box>
            <Typography sx={{ m: 0, fontFamily: DISPLAY, fontWeight: 700, fontSize: 22, color: 'var(--tl-g9)' }}>
              We&rsquo;ll call you
            </Typography>
            <Typography sx={{ m: 0, fontFamily: UI, fontSize: 15, lineHeight: 1.6, color: 'var(--tl-muted)' }}>
              Our team will ring you {when.toLowerCase()}. Keep your phone handy.
            </Typography>
            <Box
              component="span"
              sx={{
                alignSelf: 'flex-start', mt: 0.5, px: 1.5, py: 0.75, borderRadius: '999px',
                bgcolor: 'var(--tl-mint)', color: 'var(--tl-teal-d)',
                fontFamily: UI, fontSize: 13, fontWeight: 800,
              }}
            >
              Reference {done.request_id}
            </Box>
            <Box
              component="button"
              type="button"
              onClick={onClose}
              sx={{
                mt: 1.5, p: '13px 18px', borderRadius: '999px', border: 0, cursor: 'pointer',
                bgcolor: 'var(--tl-g9)', color: '#FFFFFF',
                fontFamily: UI, fontSize: 15, fontWeight: 800,
                transition: `background .3s ${EASE}`,
                '&:hover': { bgcolor: 'var(--tl-g8)' },
              }}
            >
              Done
            </Box>
          </Box>
        ) : (
          <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 1.75 }}>
            <Typography sx={{ m: 0, fontFamily: DISPLAY, fontWeight: 700, fontSize: 22, color: 'var(--tl-g9)' }}>
              Tell us when to call
            </Typography>
            <Typography sx={{ m: 0, fontFamily: UI, fontSize: 14.5, lineHeight: 1.55, color: 'var(--tl-muted)' }}>
              Leave your number and we&rsquo;ll ring you back to help you pick the right tests.
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
              <Box component="label" htmlFor="cb-phone" sx={label}>Mobile number *</Box>
              <Box
                component="input"
                id="cb-phone"
                type="tel"
                inputMode="numeric"
                autoComplete="tel"
                placeholder="+91 XXXXXXXXXX"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                sx={field}
              />
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
              <Box component="label" htmlFor="cb-name" sx={label}>Your name (optional)</Box>
              <Box
                component="input"
                id="cb-name"
                autoComplete="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                sx={field}
              />
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
              <Box component="span" sx={label}>When suits you?</Box>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {WHEN.map((w) => {
                  const on = w === when;
                  return (
                    <Box
                      key={w}
                      component="button"
                      type="button"
                      onClick={() => setWhen(w)}
                      aria-pressed={on}
                      sx={{
                        px: 1.75, py: 1, borderRadius: '999px', cursor: 'pointer',
                        fontFamily: UI, fontSize: 13.5, fontWeight: 700,
                        border: '1.5px solid',
                        borderColor: on ? 'var(--tl-teal)' : 'var(--tl-line)',
                        bgcolor: on ? 'var(--tl-mint)' : '#FFFFFF',
                        color: on ? 'var(--tl-teal-d)' : 'var(--tl-g9)',
                        transition: `border-color .25s ${EASE}, background .25s ${EASE}`,
                      }}
                    >
                      {w}
                    </Box>
                  );
                })}
              </Box>
            </Box>

            {error && (
              <Typography role="alert" sx={{ m: 0, fontFamily: UI, fontSize: 13.5, color: '#B42318' }}>
                {error}
              </Typography>
            )}

            <Box sx={{ display: 'flex', gap: 1.25, mt: 0.5 }}>
              <Box
                component="button"
                type="button"
                onClick={onClose}
                disabled={isLoading}
                sx={{
                  p: '13px 18px', borderRadius: '999px', cursor: 'pointer',
                  border: '1.5px solid var(--tl-line)', bgcolor: '#FFFFFF', color: 'var(--tl-g9)',
                  fontFamily: UI, fontSize: 15, fontWeight: 800,
                }}
              >
                Cancel
              </Box>
              <Box
                component="button"
                type="submit"
                disabled={isLoading}
                sx={{
                  flex: 1, p: '13px 18px', borderRadius: '999px', border: 0,
                  cursor: isLoading ? 'default' : 'pointer',
                  bgcolor: 'var(--tl-yellow)', color: 'var(--tl-g9)',
                  fontFamily: UI, fontSize: 15, fontWeight: 800,
                  opacity: isLoading ? 0.7 : 1,
                  transition: `opacity .3s ${EASE}`,
                }}
              >
                {isLoading ? 'Sending…' : 'Request callback'}
              </Box>
            </Box>
          </Box>
        )}
      </Box>
    </Dialog>
  );
};

export default CallbackRequestDialog;
